import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, X, Calendar, MapPin, ArrowRight,
  ChevronRight, ChevronDown, Bookmark, Share2, LayoutGrid, List, Users,
} from "lucide-react";
import { getEvents } from "../../utils/api/eventApi.js";
import { getImageUrl } from "../../utils/imageUtils.js";
import { useLanguage } from "../../context/LanguageContext.jsx";

const CONTENT = {
  ne: {
    eyebrow: "नागरिक सहभागिता",
    title1: "कार्यक्रमहरू ",
    desc: "नेपालको भिजन २०४५ लाई अगाडि बढाउने सम्बन्धमा गरिएका वा गरिने कार्यक्रमहरू।",
    statTotal: "कुल कार्यक्रम",
    statUpcoming: "आगामी",
    statPast: "विगत",
    statCurrent: "हालको",
    featured: "विशेष",
    viewEvent: "कार्यक्रम हेर्नुहोस्",
    searchPlaceholder: "कार्यक्रम खोज्नुहोस्…",
    eventsCountSuffix: "कार्यक्रम",
    loading: "कार्यक्रमहरू लोड हुँदैछ…",
    noResultsTitle: "कुनै कार्यक्रम भेटिएन",
    noResultsBody: "खोज वा फिल्टर मिलाउनुहोस्",
    resetFilters: "फिल्टर रिसेट गर्नुहोस्",
    viewDetails: "विवरण हेर्नुहोस्",
    loadMore: "थप कार्यक्रमहरू हेर्नुहोस्",
    statuses: [
      { value: "all",        label: "सबै" },
      { value: "predefined", label: "हालको" },
      { value: "upcoming",   label: "आगामी" },
      { value: "past",       label: "विगत" },
    ],
    statusConfig: {
      current:  { label: "हालको", dot: "#9ca3af", bg: "#f3f4f6", text: "#6b7280" },
      upcoming: { label: "आगामी",   dot: "#e7e7e7", bg: "rgba(47, 42, 39, 0.5)", text: "#f8fefb" },
      past:     { label: "विगत",       dot: "#9ca3af", bg: "#f3f4f6",            text: "#6b7280" },
    },
  },
  en: {
    eyebrow: "Civic Participation",
    title1: "Events ",
    desc: "Programs conducted or to be conducted in relation to advancing Nepal’s Vision 2045.",
    statTotal: "Total Events",
    statUpcoming: "Upcoming",
    statPast: "Past",
    statCurrent: "Current",
    featured: "Featured",
    viewEvent: "View Event",
    searchPlaceholder: "Search events…",
    eventsCountSuffix: "events",
    loading: "Loading events…",
    noResultsTitle: "No events found",
    noResultsBody: "Try adjusting your search or filters",
    resetFilters: "Reset Filters",
    viewDetails: "View Details",
    loadMore: "Load More Events",
    statuses: [
      { value: "all",        label: "All" },
      { value: "current", label: "Current" },
      { value: "upcoming",   label: "Upcoming" },
      { value: "past",       label: "Past" },
    ],
    statusConfig: {
      current:  { label: "Current", dot: "#9ca3af", bg: "#f3f4f6", text: "#6b7280" },
      upcoming: { label: "Upcoming", dot: "#e7e7e7", bg: "rgba(47, 42, 39, 0.5)", text: "#f8fefb" },
      past:     { label: "Past",     dot: "#9ca3af", bg: "#f3f4f6",            text: "#6b7280" },
    },
  },
};

const dateLocale = (lang) => (lang === "ne" ? "en-NP" : "en-US");


const stripHtml = (html) =>
  html ? html.replace(/<[^>]*>/g, "").trim() : "";

const EventsPage = () => {
  const { language } = useLanguage();
  const t = CONTENT[language];
  const STATUSES = t.statuses;
  const statusConfig = t.statusConfig;

  const [events,       setEvents]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search,       setSearch]       = useState("");
  const [view,         setView]         = useState("grid");
  const [saved,        setSaved]        = useState([]);
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(9);
  const navigate = useNavigate();

  const PAGE_SIZE = 9;

  // whenever the filter/search/view changes the result set, start back at page one
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeFilter, search, view]);

  useEffect(() => { fetchEvents(); }, [activeFilter]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const status = activeFilter === "all" ? null : activeFilter;
      const data = await getEvents(status);
      setEvents(data);
    } catch (error) {
      console.error("Failed to fetch events:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSave = (id) =>
    setSaved((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

    const filtered = useMemo(() =>
  events
    .filter((e) => {
      const q = search.toLowerCase();
      return (
        (e.title?.toLowerCase().includes(q) || e.location?.toLowerCase().includes(q) || e.organizer?.toLowerCase().includes(q)) &&
        (activeFilter === "all" || e.status === activeFilter)
      );
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date)),
  [events, search, activeFilter]
);

  const visibleEvents = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const featured = events.filter((e) => e.featured && e.status !== "past").slice(0,8);
  useEffect(() => {
    setFeaturedIndex(0);
  }, [activeFilter, events.length]);

  useEffect(() => {
    if (featured.length <= 1) return;
    const timer = setInterval(() => {
      setFeaturedIndex((prev) => (prev + 1) % featured.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featured.length]);

  const nextFeatured = () =>
    setFeaturedIndex((prev) => (prev + 1) % featured.length);
  const prevFeatured = () =>
    setFeaturedIndex((prev) => (prev - 1 + featured.length) % featured.length);

  return (
    <div className="min-h-screen" style={{ background: "var(--background)", fontFamily: "'Inter', sans-serif" }}>

      {/* ── Page Header ── */}
      <div style={{ background: "rgb(20, 83, 45)" }} className="pt-20 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <h1 className="text-white mb-3 mt-5"
                style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem,4vw,3.2rem)", fontWeight: 700, color: "rgb(249 115 22 / 80%)", lineHeight: 1.1 }}>
                {t.title1}
              </h1>
              <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.9rem", lineHeight: 1.7 }}>
                {t.desc}
              </p>
            </div>

            {/* Quick stats */}
            <div className="flex gap-3 flex-wrap">
              {[
                { label: t.statTotal,    value: String(events.length) },
                { label: t.statUpcoming, value: String(events.filter((e) => e.status === "upcoming").length) },
                { label: t.statPast,     value: String(events.filter((e) => e.status === "past").length) },
                { label: t.statCurrent,  value: String(events.filter((e) => e.status === "predefined").length) },
              ].map((s) => (
                <div key={s.label} className="rounded-xl px-4 py-3 border text-center min-w-[72px]"
                  style={{ background: "rgb(21, 75, 43)", borderColor: "rgba(255,255,255,0.1)" }}>
                  <p className="text-white font-bold" style={{ fontSize: "1.4rem", lineHeight: 1 }}>{s.value}</p>
                  <p style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.35)", marginTop: "3px" }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Featured Banner ── */}
      {featured.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 -mt-8 mb-10 relative z-10">
          <div className="rounded-2xl overflow-hidden border shadow-xl relative"
            style={{ borderColor: "rgba(40, 42, 41, 0.01)" }}>
            {featured[featuredIndex].imageUrl ? (
              <img src={getImageUrl(featured[featuredIndex].imageUrl)} alt={featured[featuredIndex].title} className="w-full h-64 object-cover" />
            ) : (
              <div className="w-full h-64 bg-green-900" />
            )}
            <div className="absolute inset-0"
              style={{ background: "linear-gradient(90deg, rgba(10,22,40,0.92) 0%, rgba(10,22,40,0.6) 60%, transparent 100%)" }} />
            <div className="absolute inset-0 flex items-center px-16 gap-8">
              <div className="flex-1 max-w-sm overflow-hidden">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{
                      background: (statusConfig[featured[featuredIndex].status] || statusConfig.current).bg,
                      color:      (statusConfig[featured[featuredIndex].status] || statusConfig.current).text,
                    }}>
                    <span className="w-1.5 h-1.5 rounded-full inline-block"
                      style={{ background: (statusConfig[featured[featuredIndex].status] || statusConfig.current).dot }} />
                    {(statusConfig[featured[featuredIndex].status] || statusConfig.current).label}
                  </span>
                  <span className="text-xs px-2 py-1 rounded-md"
                    style={{ background: "rgba(215, 213, 213, 0.1)", color: "rgb(224, 244, 12)" }}>
                    ★ {t.featured}
                  </span>
                </div>
                <h2 className="text-white mb-2 font-semibold leading-snug" style={{ fontSize: "1.25rem" }}>
                  {featured[featuredIndex].title}
                </h2>
                <p className="line-clamp-2 break-words"
  style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.6 }}>
  {stripHtml(featured[featuredIndex].description)}
</p>
                <div className="flex flex-wrap gap-4 mt-4">
                  <span className="flex items-center gap-1.5 text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(featured[featuredIndex].date).toLocaleDateString(dateLocale(language))}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
                    <Users className="w-3.5 h-3.5" />{featured[featuredIndex].organizer}
                  </span>
                </div>
              </div>
              <div className="hidden md:flex flex-col gap-2">
                <button
                  onClick={() => navigate(`/events/${featured[featuredIndex]._id}`)}
                  className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: "rgb(249 115 22 / 80%)" }}>
                  {t.viewEvent}
                </button>
              </div>
            </div>


            {featured.length > 1 && (
              <>
                <button
                  onClick={prevFeatured}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all"
                  style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(4px)" }}>
                  <ChevronRight className="w-4 h-4 text-white" style={{ transform: "rotate(180deg)" }} />
                </button>
                <button
                  onClick={nextFeatured}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all"
                  style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(4px)" }}>
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>


                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                  {featured.length <= 6 ? (
                    featured.map((_, i) => (
                      <button key={i} onClick={() => setFeaturedIndex(i)}
                        className="h-1.5 rounded-full transition-all"
                        style={{
                          width: i === featuredIndex ? "18px" : "6px",
                          background: i === featuredIndex ? "rgb(249 115 22 / 90%)" : "rgba(255,255,255,0.4)",
                        }} />
                    ))
                  ) : (
                    <span className="text-xs px-2.5 py-1 rounded-full"
                      style={{ background: "rgba(255,255,255,0.15)", color: "white", backdropFilter: "blur(4px)" }}>
                      {featuredIndex + 1} / {featured.length}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Search + Filter Bar ── */}
      <div className="sticky top-0 z-30 border-b"
        style={{ background: "rgba(248,249,252,0.96)", backdropFilter: "blur(12px)", borderColor: "#eef0f3" }}>
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center gap-3 flex-wrap">

            {/* Search */}
            <div className="relative flex-1 min-w-48 max-w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: "var(--muted-foreground)" }} />
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-8 py-2 rounded-xl border text-sm outline-none transition-all"
                style={{ background: "white", borderColor: "#10101066", color: "var(--foreground)", fontSize: "0.83rem" }}
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <X className="w-3.5 h-3.5" style={{ color: "var(--muted-foreground)" }} />
                </button>
              )}
            </div>

            {/* Status chips */}
            <div className="flex gap-1.5">
              {STATUSES.map(({ value, label }) => (
                <button key={value} onClick={() => setActiveFilter(value)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize"
                  style={{
                    background:   activeFilter === value ? "rgb(20 83 45)" : "white",
                    color:        activeFilter === value ? "white" : "var(--muted-foreground)",
                    border:       activeFilter === value ? "1px solid rgb(20 83 45)" : "1px solid #eef0f3",
                  }}>
                  {label}
                </button>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <p className="text-xs hidden sm:block" style={{ color: "var(--muted-foreground)" }}>
                {filtered.length} {t.eventsCountSuffix}
              </p>
              {/* View toggle */}
              <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: "#eef0f3" }}>
                {["grid", "list"].map((v) => {
                  const Icon = v === "grid" ? LayoutGrid : List;
                  return (
                    <button key={v} onClick={() => setView(v)}
                      className="p-1.5 transition-colors"
                      style={{
                        background: view === v ? "var(--secondary)" : "white",
                        color:      view === v ? "rgb(20 83 45)" : "var(--muted-foreground)",
                      }}>
                      <Icon className="w-3.5 h-3.5" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Event Grid / List ── */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        {loading ? (
          <div className="text-center py-24">
            <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>{t.loading}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <p style={{ fontSize: "2.5rem" }}>📭</p>
            <p className="mt-3 font-semibold" style={{ color: "var(--foreground)" }}>{t.noResultsTitle}</p>
            <p className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>{t.noResultsBody}</p>
            <button
              onClick={() => { setSearch(""); setActiveFilter("all"); }}
              className="mt-4 px-4 py-2 rounded-lg text-sm"
              style={{ background: "var(--secondary)", color: "rgb(20 83 45)" }}>
              {t.resetFilters}
            </button>
          </div>
        ) : view === "grid" ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {visibleEvents.map((event) => (
                <GridCard
                  key={event._id}
                  event={event}
                  saved={saved.includes(event._id)}
                  onSave={() => toggleSave(event._id)}
                  statusConfig={statusConfig}
                  viewDetailsLabel={t.viewDetails}
                  language={language}
                />
              ))}
            </div>
            {hasMore && (
              <div className="flex flex-col items-center gap-2 mt-8">
                <button
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
                  style={{ background: "var(--secondary)", color: "rgb(20 83 45)" }}>
                  <ChevronDown className="w-4 h-4" />
                  {t.loadMore}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              {visibleEvents.map((event) => (
                <ListCard
                  key={event._id}
                  event={event}
                  saved={saved.includes(event._id)}
                  onSave={() => toggleSave(event._id)}
                  statusConfig={statusConfig}
                  viewDetailsLabel={t.viewDetails}
                  language={language}
                />
              ))}
            </div>
            {hasMore && (
              <div className="flex flex-col items-center gap-2 mt-8">
                <button
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
                  style={{ background: "var(--secondary)", color: "rgb(20 83 45)" }}>
                  <ChevronDown className="w-4 h-4" />
                  {t.loadMore}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};



/* ── Grid Card ── */
function GridCard({ event, saved, onSave, statusConfig, viewDetailsLabel, language }) {
  const [hov, setHov] = useState(false);
  const cfg = statusConfig[event.status] || statusConfig.current;
  const navigate = useNavigate();

  return (
    <div
      className="rounded-2xl border overflow-hidden flex flex-col transition-all duration-250"
      style={{
        background:   "var(--card)",
        borderColor:  hov ? "rgba(20,83,45,0.3)" : "#eef0f3",
        boxShadow:    hov ? "0 12px 32px rgba(0,0,0,0.1)" : "0 1px 4px rgba(0,0,0,0.04)",
        transform:    hov ? "translateY(-4px)" : "none",
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}>

      {/* Image */}
      <div className="relative h-44 overflow-hidden bg-gray-100">
        {event.imageUrl ? (
          <img src={getImageUrl(event.imageUrl)} alt={event.title}
            className="w-full h-full object-cover transition-transform duration-500"
            style={{ transform: hov ? "scale(1.05)" : "scale(1)" }} />
        ) : (
          <div className="w-full h-full bg-green-900 flex items-center justify-center">
            <span className="text-white/30 text-4xl">🏛️</span>
          </div>
        )}
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.35) 100%)" }} />

        {/* Status badge */}
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ background: cfg.bg, border: `1px solid ${cfg.dot}40`, color: cfg.text }}>
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: cfg.dot }} />
            {cfg.label}
          </span>
        </div>

      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold leading-snug mb-2 flex-1"
          style={{ fontSize: "0.9rem", color: "var(--foreground)" }}>
          {event.title}
        </h3>
        <p className="mb-3 leading-relaxed line-clamp-2"
          style={{ fontSize: "0.74rem", color: "var(--muted-foreground)", lineHeight: 1.6 }}>
          {stripHtml(event.description)}
        </p>

        <div className="space-y-1.5 mb-4">
          <div className="flex items-center gap-2" style={{ fontSize: "0.73rem", color: "var(--muted-foreground)" }}>
            <Calendar className="w-3.5 h-3.5 shrink-0" style={{ color: "rgb(20 83 45)" }} />
            {new Date(event.date).toLocaleDateString(dateLocale(language))}
          </div>
          <div className="flex items-center gap-2" style={{ fontSize: "0.73rem", color: "var(--muted-foreground)" }}>
            <Users className="w-3.5 h-3.5 shrink-0" style={{ color: "rgb(20 83 45)" }} />
            {event.organizer}
          </div>
        </div>

        <button
          onClick={() => navigate(`/events/${event._id}`)}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all"
          style={{
            background: hov ? "rgb(20 83 45)" : "var(--secondary)",
            color:      hov ? "white" : "rgb(20 83 45)",
          }}>
           {viewDetailsLabel}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ── List Card ── */
function ListCard({ event, saved, onSave, statusConfig, viewDetailsLabel, language }) {
  const [hov, setHov] = useState(false);
  const cfg = statusConfig[event.status] || statusConfig.current;
  const navigate = useNavigate();

  return (
    <div
      className="rounded-2xl border flex gap-0 overflow-hidden transition-all duration-200"
      style={{
        background:  "var(--card)",
        borderColor: hov ? "rgba(20,83,45,0.3)" : "#eef0f3",
        boxShadow:   hov ? "0 6px 20px rgba(0,0,0,0.08)" : "0 1px 3px rgba(0,0,0,0.04)",
        transform:   hov ? "translateY(-2px)" : "none",
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}>

      {/* Accent line */}
      <div className="w-1 shrink-0 transition-colors"
        style={{ background: hov ? "rgb(20 83 45)" : "transparent" }} />

      {/* Image */}
      <div className="hidden sm:block w-36 shrink-0 overflow-hidden bg-gray-100">
        {event.imageUrl ? (
          <img src={getImageUrl(event.imageUrl)} alt={event.title}
            className="w-full h-full object-cover transition-transform duration-500"
            style={{ transform: hov ? "scale(1.05)" : "scale(1)" }} />
        ) : (
          <div className="w-full h-full bg-green-900 flex items-center justify-center">
            <span className="text-white/30 text-2xl">🏛️</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 p-4 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ background: cfg.bg, border: `1px solid ${cfg.dot}40`, color: cfg.text }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: cfg.dot }} />
              {cfg.label}
            </span>
          </div>
          <h3 className="font-semibold mb-1 leading-snug"
            style={{ fontSize: "0.92rem", color: "var(--foreground)" }}>
            {event.title}
          </h3>
          <p className="line-clamp-1 mb-2"
            style={{ fontSize: "0.75rem", color: "var(--muted-foreground)" }}>
            {stripHtml(event.description)}
          </p>
          <div className="flex flex-wrap gap-4">
            {[
              { Icon: Calendar, text: new Date(event.date).toLocaleDateString(dateLocale(language)) },
              { Icon: Users,    text: event.organizer },
            ].map(({ Icon, text }) => (
              <span key={text} className="flex items-center gap-1.5"
                style={{ fontSize: "0.72rem", color: "var(--muted-foreground)" }}>
                <Icon className="w-3 h-3" style={{ color: "rgb(20 83 45)" }} />{text}
              </span>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 shrink-0">

          <button
            onClick={() => navigate(`/events/${event._id}`)}
            className="flex items-center gap-1 px-4 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap"
            style={{
              background: hov ? "rgb(20 83 45)" : "var(--secondary)",
              color:      hov ? "white" : "rgb(20 83 45)",
            }}>
            {viewDetailsLabel}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
export default EventsPage;